import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Mail, Phone, Hash, AlertCircle } from 'lucide-react'
import { useSessionStore, useUIStore } from '@/stores'
import { authService } from '@services/authService'
import { HTTP_STATUS, InputState, SessionStatus, MatchField, HtmlInputType } from '@/constants'
import { CustomerMatchMessages } from '@constants/loadingMessages'
import { emitProcessingMessages, clearProcessingMessages } from '@/utils/processing'
import CustomerVerify from '@/components/steps/CustomerVerify'
import { GlobalLoading } from '@components/LoadingSpinner'
import OmpHeader from '../sections/OmpHeader'
import OmpFooter from '../sections/OmpFooter'
import OmpCard from '../components/OmpCard'
import OmpButton from '../components/OmpButton'
import OmpLifeline from '../OmpLifeline'
import { applyOmpTheme, getThemeVariables } from '../utils/themeTokens'
import { OMP_PAGE_LOADERS } from '../pageLoaders'

const FIELD_META: Record<MatchField, { label: string; placeholder: string; type: HtmlInputType; icon: typeof Mail }> = {
  [MatchField.Email]: {
    label: 'Email Address',
    placeholder: 'example@gmail.com',
    type: HtmlInputType.Email,
    icon: Mail,
  },
  [MatchField.Phone]: { label: 'Phone Number', placeholder: '555-555-5555', type: HtmlInputType.Tel, icon: Phone },
  [MatchField.OrderNumber]: { label: 'Order Number', placeholder: '123456', type: HtmlInputType.Text, icon: Hash },
}

const schema = z
  .object({ email: z.string().optional(), phone: z.string().optional(), order_number: z.string().optional() })
  .refine(d => Object.values(d).some(v => v && v.trim().length > 0), { message: 'Complete one field to continue' })

type LoginFormValues = z.infer<typeof schema>

const OmpLogin = () => {
  const navigate = useNavigate()
  const session = useSessionStore(s => s.session) as any
  const campaign = useSessionStore(s => s.campaign) as any
  const inputState = useSessionStore(s => s.inputState)
  const setSession = useSessionStore(s => s.setSession)
  const setInputState = useSessionStore(s => s.setInputState)
  const isSessionUserKnown = useSessionStore(s => s.isSessionUserKnown)
  const noMatch = useSessionStore(s => s.noMatch)
  const setLoading = useUIStore(s => s.setLoading)
  const [matchError, setMatchError] = useState<string | null>(null)
  const [retryCnt, setRetryCnt] = useState(0)

  const themeVariables = getThemeVariables(campaign)
  useEffect(() => {
    applyOmpTheme(themeVariables)
  }, [themeVariables])

  useEffect(() => () => clearProcessingMessages(), [])

  const matchFields = useMemo<MatchField[]>(() => {
    const fields = (campaign?.match_fields as MatchField[]) ?? []
    return fields.length ? fields : [MatchField.Email, MatchField.Phone]
  }, [campaign])

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { email: '', phone: '', order_number: '' },
  })

  const values = watch()
  const hasAnyValue = matchFields.some(f => (values[f] || '').trim().length > 0)

  // CustomerVerify leaves the loader up for the funnel's chained call; nothing follows here.
  const goToPortal = () => {
    OMP_PAGE_LOADERS.home().catch(() => {})
    clearProcessingMessages()
    setLoading(false)
    navigate('/portal', { replace: true })
  }

  const leaveVerify = () => {
    clearProcessingMessages()
    setLoading(false)
    setInputState(InputState.Unknown)
  }

  const onSubmit = async (formValues: LoginFormValues) => {
    if (!session?.id) {
      return
    }
    setMatchError(null)
    setLoading(true)
    emitProcessingMessages(CustomerMatchMessages)
    const params: Record<string, string> = {}
    matchFields.forEach(f => {
      params[f] = (formValues[f] || '').trim()
    })
    try {
      const result = await authService.match(session.id, params)
      setSession(result)
      clearProcessingMessages()
      setLoading(false)
      const status = Number((result as any)?.status)
      if (isSessionUserKnown()) {
        goToPortal()
      } else if (status === SessionStatus.Unverified || status === SessionStatus.Matched) {
        setInputState(InputState.NeedsVerification)
      }
    } catch (err: any) {
      clearProcessingMessages()
      setLoading(false)
      const code = err?.originalError?.response?.status ?? err?.response?.status
      if (code !== HTTP_STATUS.NOT_FOUND && code !== HTTP_STATUS.FORBIDDEN) {
        setMatchError('Something went wrong. Please try again.')
        return
      }
      const message = "Sorry, we couldn't find your account. Please double-check your details."
      setMatchError(message)
      const newRetryCnt = retryCnt + 1
      setRetryCnt(newRetryCnt)
      if (code === HTTP_STATUS.NOT_FOUND && newRetryCnt < session?.lifeline_unmatched_count) {
        return
      }
      noMatch(message)
    }
  }

  const showVerify = inputState === InputState.NeedsVerification

  return (
    <div className="omp-root omp-auth-root">
      <OmpHeader />
      <main className="omp-auth-main">
        <OmpCard className="omp-auth-card">
          {showVerify ? (
            <div className="omp-verify">
              <CustomerVerify onDone={goToPortal} onBack={leaveVerify} />
            </div>
          ) : (
            <form className="omp-auth-form" onSubmit={handleSubmit(onSubmit)}>
              <div className="omp-auth-titles">
                <h1 className="omp-auth-title">Let's find your account!</h1>
                <p className="omp-auth-subtitle">Complete one field to continue</p>
              </div>

              {matchError && (
                <div className="omp-auth-error" role="alert">
                  <AlertCircle size={16} />
                  {matchError}
                </div>
              )}

              <div className="omp-auth-fields">
                {matchFields.map((field, index) => {
                  const meta = FIELD_META[field]
                  const Icon = meta.icon
                  const fieldValue = (values[field] || '').trim()
                  const showField = !!fieldValue || !hasAnyValue
                  return (
                    <div key={field} className="omp-auth-field-group">
                      {index > 0 && !hasAnyValue && (
                        <div className="omp-auth-or">
                          <span className="omp-auth-or-rule" />
                          <span className="omp-auth-or-text">Or</span>
                          <span className="omp-auth-or-rule" />
                        </div>
                      )}
                      {showField && (
                        <>
                          <label className="omp-auth-label">{meta.label}</label>
                          <div className="omp-input-icon">
                            <Icon size={20} className="omp-input-icon-glyph" />
                            <input
                              className="form-control omp-input-with-icon"
                              type={meta.type}
                              placeholder={meta.placeholder}
                              autoComplete={
                                field === MatchField.Email ? 'email' : field === MatchField.Phone ? 'tel' : 'off'
                              }
                              {...register(field)}
                            />
                          </div>
                        </>
                      )}
                    </div>
                  )
                })}
              </div>

              {!hasAnyValue && errors.root?.message && <p className="omp-form-error">{errors.root.message}</p>}

              <OmpButton variant="primary" fullWidth type="submit" disabled={!hasAnyValue || isSubmitting}>
                Submit
              </OmpButton>
            </form>
          )}
        </OmpCard>
      </main>
      <OmpFooter />
      <OmpLifeline resyncOnClose={false} />
      <GlobalLoading />
    </div>
  )
}

export default OmpLogin
